import { ORBITS, smooth, TAU } from "../orbit";
import type { InstrumentId } from "../score";
import { CLIMAX, FINAL_BEAT, PARTS, timeBeat, type Note } from "./score";
export { ORBITS };
export const entryWeight = (id: InstrumentId, time: number) =>
  id === "marimba" ? 1 : smooth((time - (PARTS[id][0].time - 0.24)) / 0.24);
/** Integrated beat phase makes every marker cross 12 o'clock at its own attack,
 * even while tempo varies. Its lifetime is strictly shorter than one orbit. */
export const markerAngle = (e: Note, time: number) =>
  -Math.PI / 2 +
  (TAU * (timeBeat(e.finale ? Math.min(time, CLIMAX) : time) - e.startBeat)) /
    4;
export const markerVisible = (e: Note, time: number) => {
  const delta = timeBeat(time) - e.startBeat;
  return delta > -3.75 && (delta < 0.15 || (e.finale && time < 18));
};
export const position = (e: Note, time: number, radius: number) => ({
  x: Math.cos(markerAngle(e, time)) * radius,
  y: Math.sin(markerAngle(e, time)) * radius,
});
export const hit = (e: Note, time: number, decay: number) =>
  time < e.time ? 0 : e.velocity * Math.exp(-(time - e.time) / decay);
export const latest = (id: InstrumentId, time: number) =>
  [...PARTS[id]].reverse().find((e) => e.time <= time);
export const finalePulse = (time: number) =>
  time < CLIMAX ? 0 : Math.exp(-(time - CLIMAX) / 0.52);
export const rotation = (time: number) => {
  const b = timeBeat(Math.min(time, CLIMAX));
  // Arrive at zero with all markers and ease into a held, resolved tableau.
  return (
    (time <= CLIMAX
      ? b
      : FINAL_BEAT + 0.42 * (1 - Math.exp(-(time - CLIMAX) * 3))) * 90
  );
};
/** Attack strength changes displacement AND scale. No accumulated simulation. */
export const iconPose = (id: InstrumentId, time: number) => {
  const e = latest(id, time);
  if (!e)
    return { jump: 0, scale: 1, rotate: 0, glow: 0, age: 10, velocity: 0 };
  const age = time - e.time,
    v = e.velocity;
  const duration = e.finale
    ? 0.66
    : { marimba: 0.29, bell: 0.58, bass: 0.42, percussion: 0.21 }[id];
  const u = Math.min(1, age / duration);
  const jump =
    u >= 1
      ? 0
      : Math.sin(Math.PI * u) ** 1.2 *
        v *
        (e.finale
          ? 52
          : { marimba: 39, bell: 28, bass: 44, percussion: 30 }[id]);
  const elastic = Math.sin(u * Math.PI * 2) * Math.exp(-u * 2.8);
  const scale =
    1 +
    v * (0.31 * Math.sin(Math.PI * u) + 0.12 * elastic) -
    (id === "marimba" && !e.finale ? 0.065 * v * Math.exp(-age / 0.022) : 0);
  return {
    jump,
    scale,
    rotate:
      (id === "bell" ? 13 : id === "percussion" ? 17 : 0) *
      v *
      Math.sin(u * Math.PI * 3) *
      Math.exp(-u * 2),
    glow: hit(e, time, id === "bell" ? 0.33 : 0.13),
    age,
    velocity: v,
  };
};
